import Head from "next/head";
import React, {useEffect, useState} from "react";
import {useQuery} from "@apollo/react-hooks";
import CAMPER_INFO from "../../../graphql/queries/camperInfo";
import {useRouter} from "next/router";
import {getDataFromTree} from "@apollo/react-ssr";
import SwiperGallery from "../../../components/Camper/Details/SwiperGallery";
import withApollo from "../../../lib/withApollo";
import {getImageUrl} from "../../../services/image";
import {Button, Card, CardBody, Col, Container, Input, Row} from "reactstrap";
import SelectionForm from "../../../components/Camper/Details/SelectionForm";
import data from "../../../data/detail-rooms.json";
import Header from "../../../components/Header";
import CamperDetailHeader from "../../../components/Header/CamperDetail/Mobile/Index";
import {SearchSelection} from "../../../types/camper/searchSelection";
import dynamic from "next/dynamic";
import BookingButtons from "../../../components/Camper/Details/BookingButtons";

const availableTypes = [
    'voyager',
    'familie-camper',
    'luxe-camper',
]

let CamperSearchModal = dynamic(
    () => import("../../../components/Camper/Search/Mobile/CamperSearchModal"),
    {ssr: false}
);

const encodeBookingDetails = (details) => {
    return Buffer.from(JSON.stringify(details)).toString("base64");
};

const Index = (props) => {
    const router = useRouter();
    const query = router.query;
    const [camperPickupLocations, setCamperPickupLocations] = useState([]);
    const [options, setOptions] = useState(null);
    const [searching, setSearching] = useState(false);
    const [selectedOptionIds, setSelectedOptionIds] = useState<number[]>([]);
    const [selectedInsuranceId, setSelectedInsuranceId] = useState<number>(query.index === 'voyager' ? 353 : 352);
    const [totalPrice, setTotalPrice] = useState<number>(null);
    const [modal, setModal] = useState(false);
    const [searchSelection, setSearchSelection] = useState(new SearchSelection())
    const toggleModal = () => {
        setModal(!modal)
    };

    const {loading, error, data} = useQuery(CAMPER_INFO, {
        variables: {
            camperSeoId: query.index,
        },
    });

    useEffect(() => {
        if (!options) {
            return;
        }

        const camper = getCamper();
        if (!camper) {
            return;
        }

        const camperBasePrice = camper.totalRate;
        const optionsPrice = selectedOptionIds.reduce((acc, optionId) => {
            return acc + options.optionalFees.find(option => option.id === optionId).totalFeeEuro;
        }, 0)
        const insurancePrice = getSelectedInsurance() ? getSelectedInsurance().totalAmountEuro : 0;

        setTotalPrice(camperBasePrice + optionsPrice + insurancePrice);
    }, [selectedOptionIds, options, selectedInsuranceId])

    useEffect(() => {
        if (!(data && data.CamperPickupLocations && data.CamperPickupLocations.locations)) {
            return;
        }
        const camperPickupLocations = data.CamperPickupLocations.locations.map(location => {
            return {
                label: location.location,
                value: location.id,
                seoId: location.seoId,
            }
        });

        setCamperPickupLocations(camperPickupLocations);
    }, [data]);

    useEffect(() => {
        setSearchSelection(SearchSelection.fromUrlParams(router));
    }, [router.query])

    if (!data) {
        return <div>Gegevens aan het ophalen</div>;
    }

    const camperInfo = data.CamperInfo;
    const images = camperInfo.images.map(image => {
            return {
                img: getImageUrl(image),
                alt: camperInfo.type
            }
        }
    )

    const getCamper = () => {
        if (!options) {
            return;
        }

        return options.campers.find(camper => camper.seoId === query.index);
    }

    const getCamperBasePrice = (): number => {
        const camper = getCamper();
        if (!camper) {
            return 0;
        }

        return camper.totalRate;
    }

    const isOptionSelected = (optionId: number): boolean => {
        return !!selectedOptionIds.find(selectedOptionId => selectedOptionId === optionId);
    }

    const toggleOption = (optionId: number): void => {
        const option = selectedOptionIds.find(selectedOptionId => selectedOptionId === optionId);

        if (option) {
            const newOptions = selectedOptionIds.filter(selectedOptionId => selectedOptionId !== optionId);
            setSelectedOptionIds(newOptions);
        }

        if (!option) {
            const newOptions = [
                ...selectedOptionIds,
                optionId
            ];
            setSelectedOptionIds(newOptions);
        }
    }

    const addOption = (optionId: number): void => {
        const newOptions = [
            ...selectedOptionIds,
            optionId
        ];
        setSelectedOptionIds(newOptions);
    }

    const removeOption = (optionId: number): void => {
        const optionIds = selectedOptionIds.filter(selectedOptionId => selectedOptionId === optionId);
        optionIds.pop();

        const newOptions = [
            ...selectedOptionIds.filter(selectedOptionId => selectedOptionId !== optionId),
            ...optionIds
        ];
        setSelectedOptionIds(newOptions);
    }

    const optionCount = (optionId: number): number => {
        const optionIds = selectedOptionIds.filter(selectedOptionId => selectedOptionId === optionId);

        return optionIds.length;
    }

    const getSelectedOptions = () => {
        return selectedOptionIds.map(optionId => options.optionalFees.find(option => option.id === optionId));
    }

    const getSelectedInsurance = () => {
        if (!options) {
            return;
        }

        return options.insuranceOptions.find(option => option.id === selectedInsuranceId)
    }

    return (
        <>
            <Head>
                <title>
                    {camperInfo.type} - Cottage Centre Campers
                </title>
                <meta name="viewport" content="initial-scale=1.0, width=device-width"/>
                <meta
                    name="description"
                    content={``}
                />
            </Head>

            <div className="d-none d-lg-block">
                <Header  {...props} />
            </div>

            <div className="d-lg-none">
                <CamperDetailHeader
                    price={100}
                    toggleModal={toggleModal}
                />
            </div>
            <section>
                <div className="position-relative">
                    {camperInfo.images && <SwiperGallery data={images}/>}
                </div>

                <Container className="py-4 d-lg-none">
                    {getCamperBasePrice() > 0 && !searching &&
                        <BookingButtons
                            searchSelection={searchSelection}
                            encodedBookingDetails={encodeBookingDetails({
                                slug: query.index,
                                startDate: searchSelection.pickupDate
                                    ? searchSelection.formattedPickupDate()
                                    : null,
                                endDate: searchSelection.dropOffDate
                                    ? searchSelection.formattedDropOffDate()
                                    : null,
                            })}
                            price={totalPrice}
                            camperSeoId={router.query.index[0]}
                            selectedOptions={getSelectedOptions()}
                            selectedInsurance={getSelectedInsurance()}
                            camperInfo={camperInfo}
                            camperBasePrice={getCamperBasePrice()}
                        />
                    }

                    {(getCamperBasePrice() < 1 || searching) &&
                        <>
                            {searchSelection.isValidSelection() &&
                                <>
                                    {searching ?
                                        <Row className="mb-2">
                                            <Col>
                                                <strong className="text-sm">
                                                    Beschikbaarheid aan het ophalen...
                                                </strong>
                                                <strong>
                                                </strong>
                                            </Col>
                                        </Row>
                                        :
                                        <Row className="mb-2">
                                            <Col>
                                                <strong className="text-sm">
                                                    Geen campers beschikbaar voor selectie
                                                </strong>
                                                <strong>
                                                </strong>
                                            </Col>
                                        </Row>
                                    }
                                </>
                            }

                            {!searching &&
                            <Row>
                                <Col>
                                    <Button
                                        onClick={() => toggleModal()}
                                        size="sm"
                                        aria-label="Camper beschikbaarheid"
                                        className="book-button"
                                        color="primary"
                                        block
                                    >
                                        Bekijk beschikbaarheid en prijs
                                        {" "}
                                        <i className="fas fa-calendar-alt"/>
                                    </Button>
                                </Col>
                            </Row>
                            }
                        </>
                    }
                </Container>

                <Container className="py-lg-5">
                    <Row>
                        <Col lg="7">
                            <div className="text-block mt-4 mt-md-0">
                                <h1>{camperInfo.type}</h1>

                                {camperInfo.tags && (
                                    <ul className="list-inline text-sm mb-4">
                                        {camperInfo.tags.map((tag) => (
                                            <li key={tag.value} className="list-inline-item mr-3">
                                                <i
                                                    className={`fa fa-${tag.icon} mr-1 text-secondary`}
                                                />{" "}
                                                {tag.value}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <p
                                    dangerouslySetInnerHTML={{__html: camperInfo.description}}
                                ></p>
                            </div>

                            {searching &&
                                <div className="text-block">
                                    <Row className="mt-4">
                                        <Col>
                                            Beschikbaarheid aan het ophalen
                                        </Col>
                                    </Row>
                                </div>
                            }

                            {getCamperBasePrice() < 1 && searchSelection.isValidSelection() && !searching &&
                                <div className="text-block">
                                    <Row className="mt-4">
                                        <Col>
                                            Deze camper is niet beschikbaar voor deze data en afhaallocaties
                                        </Col>
                                    </Row>
                                </div>
                            }

                            {options && getCamperBasePrice() > 0 && !searching &&
                                <div className="text-block">
                                    <>
                                        <h5>Verzekeringen</h5>
                                        {loading &&
                                            <div>Verzekering opties aan het ophalen</div>
                                        }
                                        {!loading &&
                                            <>
                                                {options.insuranceOptions && options.insuranceOptions
                                                    .filter(insuranceOption => insuranceOption.id !== 344)
                                                    .map(inuranceOption => (
                                                        <Card
                                                            outline
                                                            color={selectedInsuranceId === inuranceOption.id ? 'primary' : ''}
                                                            key={inuranceOption.id}
                                                            className="mt-4"
                                                        >
                                                            <CardBody>
                                                                <strong>{inuranceOption.name}</strong>
                                                                <ul className="list-unstyled text-sm">
                                                                    {inuranceOption.feedescription &&
                                                                        <li>
                                                                            <i className="fas fa-check text-primary mr-2"/>
                                                                            {inuranceOption.feedescription}
                                                                        </li>
                                                                    }
                                                                    {inuranceOption.feedescription1 &&
                                                                        <li>
                                                                            <i className="fas fa-check text-primary mr-2"/>
                                                                            {inuranceOption.feedescription1}
                                                                        </li>
                                                                    }
                                                                    {inuranceOption.feedescription2 &&
                                                                        <li>
                                                                            <i className="fas fa-check text-primary mr-2"/>
                                                                            {inuranceOption.feedescription2}
                                                                        </li>
                                                                    }
                                                                    {inuranceOption.feedescription3 &&
                                                                        <li>
                                                                            <i className="fas fa-check text-primary mr-2"/>
                                                                            {inuranceOption.feedescription3}
                                                                        </li>
                                                                    }
                                                                </ul>


                                                                <>
                                                                    <Button
                                                                        outline
                                                                        color="primary"
                                                                        disabled={selectedInsuranceId === inuranceOption.id}
                                                                        size="sm"
                                                                        onClick={() => setSelectedInsuranceId(inuranceOption.id)}
                                                                    >
                                                                        {selectedInsuranceId !== inuranceOption.id &&
                                                                            <><i
                                                                                className="fas fa-shopping-cart"/> Kiezen</>
                                                                        }

                                                                        {selectedInsuranceId === inuranceOption.id &&
                                                                            <><i
                                                                                className="fas fa-trash"/> Geselecteerd</>
                                                                        }
                                                                    </Button>
                                                                    <span
                                                                        className="pull-right ml-5 font-weight-bold">€ {inuranceOption.totalAmountEuro},-</span>
                                                                </>
                                                            </CardBody>
                                                        </Card>
                                                    ))}
                                            </>
                                        }
                                    </>
                                </div>
                            }


                            {options && getCamperBasePrice() > 0 && !searching &&
                                <div className="text-block">
                                    <>
                                        <h5>Extra opties</h5>
                                        {loading &&
                                            <div>Extra opties aan het ophalen</div>
                                        }

                                        {!loading &&
                                            <>
                                                {options.optionalFees && options.optionalFees.map(option => (
                                                    <Card
                                                        outline
                                                        color={isOptionSelected(option.id) ? 'primary' : ''}
                                                        key={option.id}
                                                        className="mt-4"
                                                    >
                                                        <CardBody>
                                                            <strong>{option.name}</strong>
                                                            <ul className="list-unstyled text-sm">
                                                                {option.feedescription1 &&
                                                                    <li>
                                                                        <i className="fas fa-check text-primary mr-2"/>
                                                                        {option.feedescription1}
                                                                    </li>
                                                                }
                                                                {option.feedescription2 &&
                                                                    <li>
                                                                        <i className="fas fa-check text-primary mr-2"/>
                                                                        {option.feedescription2}
                                                                    </li>
                                                                }
                                                                {option.feedescription3 &&
                                                                    <li>
                                                                        <i className="fas fa-check text-primary mr-2"/>
                                                                        {option.feedescription3}
                                                                    </li>
                                                                }
                                                            </ul>

                                                            {option.qtyapply &&
                                                                <div className="d-flex align-items-center">
                                                                    <Button
                                                                        color="items"
                                                                        className="btn-items-decrease"
                                                                        onClick={() =>
                                                                            removeOption(option.id)
                                                                        }
                                                                    >
                                                                        -
                                                                    </Button>
                                                                    <Input
                                                                        type="text"
                                                                        value={`${optionCount(option.id)}`}
                                                                        disabled
                                                                        className="input-items input-items-greaterthan"
                                                                    />
                                                                    <Button
                                                                        color="items"
                                                                        className="btn-items-increase"
                                                                        onClick={() => addOption(option.id)}
                                                                    >
                                                                        +
                                                                    </Button>
                                                                    <span
                                                                        className="pull-right ml-5 font-weight-bold">€ {option.totalFeeEuro},-</span>
                                                                </div>
                                                            }

                                                            {!option.qtyapply &&
                                                                <>
                                                                    <Button
                                                                        outline
                                                                        color={isOptionSelected(option.id) ? "info" : "primary"}
                                                                        size="sm"
                                                                        onClick={() => toggleOption(option.id)}
                                                                    >
                                                                        {!isOptionSelected(option.id) &&
                                                                            <><i
                                                                                className="fas fa-shopping-cart"/> Toevoegen</>
                                                                        }

                                                                        {isOptionSelected(option.id) &&
                                                                            <><i
                                                                                className="fas fa-trash"/> Verwijderen</>
                                                                        }
                                                                    </Button>
                                                                    <span
                                                                        className="pull-right ml-5 font-weight-bold">€ {option.totalFeeEuro},-</span>
                                                                </>
                                                            }
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                            </>
                                        }
                                    </>
                                </div>
                            }
                        </Col>
                        <Col lg="5">
                            <div
                                style={{top: "100px"}}
                                className="p-4 shadow ml-lg-4 rounded sticky-top d-none d-lg-block"
                            >
                                <SelectionForm
                                    setOptions={setOptions}
                                    options={options}
                                    totalPrice={totalPrice}
                                    selectedOptions={getSelectedOptions()}
                                    selectedInsurance={getSelectedInsurance()}
                                    camperInfo={camperInfo}
                                    camperBasePrice={getCamperBasePrice()}
                                    setLoading={setSearching}
                                    searching={searching}
                                />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {modal &&
                <CamperSearchModal
                    initialPickupLocation={camperPickupLocations.find(location => location.label === searchSelection.pickupLocation)}
                    initialDropOffLocation={camperPickupLocations.find(location => location.label === searchSelection.dropOffLocation)}
                    initialPickupDate={searchSelection.pickupDate}
                    initialDropOffDate={searchSelection.dropOffDate}
                    locations={camperPickupLocations}
                    isOpen={modal}
                    toggle={() => toggleModal()}
                />
            }
        </>
    )
};

Index.getInitialProps = async () => {
    return {
        nav: {
            light: true,
            classes: "shadow",
            color: "white",
        },
        title: data.title,
        showBackButton: true,
        paddingTop: '70px',
        hideHeader: true,
    };
};

export default withApollo(Index,
    {
        getDataFromTree
    }
);
